<template>
  <div class="leadership-wrapper">
    <div class="leadership-container">
      <div class="leadership-section">
        <h3>Club Leadership</h3>

        <!-- Filter Section -->
        <div class="filter-section">
          <div class="filter-row">
            <el-form :inline="true" size="small">
              <el-form-item label="Filter by Leadership Class">
                <el-select
                  v-model="filterLeadershipClass"
                  placeholder="Select class year"
                  style="width: 150px">
                  <el-option
                    label="All"
                    value="">
                  </el-option>
                  <el-option
                    v-for="year in classYearOptions"
                    :key="year"
                    :label="year"
                    :value="year">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-form>

            <el-alert
              title="Note: Class year usually refers to the year when the position is taken if tenure across years."
              type="info"
              :closable="false"
              show-icon
              style="flex: 1; margin-left: 16px;">
            </el-alert>
          </div>
        </div>

        <el-table :data="filteredLeadershipPositions" style="width: 100%; margin-top: 20px;">
          <el-table-column label="Position" min-width="200">
            <template slot-scope="scope">
              <el-input
                v-if="scope.row.isEditing && !isStudent"
                v-model="scope.row.name"
                size="small"
              />
              <span v-else>{{ scope.row.name }}</span>
            </template>
          </el-table-column>

          <el-table-column label="Member" min-width="300">
            <template slot-scope="scope">
              <el-select
                v-if="scope.row.isEditing && !isStudent"
                v-model="scope.row.userId"
                filterable
                remote
                reserve-keyword
                :placeholder="`Select member for the ${scope.row.name.toLowerCase()}`"
                :remote-method="searchMembers"
                :loading="loading"
                @change="(value) => handleMemberSelect(value, scope.row)"
                @focus="ensureCurrentMemberInOptions(scope.row)"
                style="width: 100%">
                <el-option
                  v-for="member in getMemberOptionsForRow(scope.row)"
                  :key="member.user_id"
                  :label="member.user.student_givenName_pinyin + ' ' + member.user.student_lastName_pinyin + ' (' + member.user.student_email + ')'"
                  :value="member.user_id">
                </el-option>
              </el-select>
              <span v-else>
                {{ getUserDisplayName(scope.row) }}
              </span>
            </template>
          </el-table-column>

          <el-table-column label="Tenure Start" min-width="150">
            <template slot-scope="scope">
              <el-date-picker
                v-if="scope.row.isEditing && !isStudent"
                v-model="scope.row.tenure_start"
                type="date"
                placeholder="Start date"
                value-format="yyyy-MM-dd"
                size="small"
                style="width: 100%">
              </el-date-picker>
              <span v-else>{{ scope.row.tenure_start || '-' }}</span>
            </template>
          </el-table-column>

          <el-table-column label="Tenure End" min-width="150">
            <template slot-scope="scope">
              <el-date-picker
                v-if="scope.row.isEditing && !isStudent"
                v-model="scope.row.tenure_end"
                type="date"
                placeholder="End date"
                value-format="yyyy-MM-dd"
                size="small"
                style="width: 100%">
              </el-date-picker>
              <span v-else>{{ scope.row.tenure_end || '-' }}</span>
            </template>
          </el-table-column>

          <el-table-column label="Class Year" min-width="120">
            <template slot-scope="scope">
              <el-select
                v-if="scope.row.isEditing && !isStudent"
                v-model="scope.row.leadership_class"
                placeholder="Year"
                clearable
                size="small"
                style="width: 100%">
                <el-option
                  v-for="year in classYearOptions"
                  :key="year"
                  :label="year"
                  :value="year">
                </el-option>
              </el-select>
              <span v-else>{{ scope.row.leadership_class || '-' }}</span>
            </template>
          </el-table-column>

          <el-table-column label="Actions" min-width="280">
            <template slot-scope="scope">
              <template v-if="!isStudent">
                <el-button
                  type="text"
                  @click="toggleEdit(scope.$index)">
                  {{ scope.row.isEditing ? 'Done' : 'Edit' }}
                </el-button>
                <el-button
                  type="text"
                  @click="removePosition(scope.$index)"
                  style="color: #F56C6C;">
                  Delete
                </el-button>
              </template>
              <el-button
                v-if="scope.row.userId && (!isStudent || isCurrentUser(scope.row))"
                :disabled="!canViewCertificate(scope.row)"
                type="text"
                @click="viewCertificate(scope.row)"
                :style="{ color: canViewCertificate(scope.row) ? '' : '#C0C4CC', cursor: canViewCertificate(scope.row) ? 'pointer' : 'not-allowed' }">
                Certificate
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="leadership-actions" v-if="!isStudent">
          <el-button type="text" icon="el-icon-plus" @click="addPosition">Add Position</el-button>
          <el-button type="primary" @click="saveLeadership">Save Changes</el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { postRequest } from '@/api/eclub'
import { getUserId, getUserType, getUserEmail } from '@/utils/store'

export default {
  name: 'Leadership',
  props: {
    clubInfo: {
      type: Object,
      default: () => ({}),
    },
    club_id: {
      type: String,
      required: true
    }
  },
  mounted() {
    console.log('Club Info:', this.clubInfo)
  },

  data() {
    const currentYear = new Date().getFullYear()
    const classYears = []
    // Sort from latest to earliest (current year + 3 down to current year - 3)
    for (let i = 3; i >= -3; i--) {
      classYears.push(currentYear + i)
    }

    return {
      leadershipPositions: [],
      memberOptions: [],
      loading: false,
      selectedMembers: {},
      filterLeadershipClass: '', // Default to empty string for "All"
      classYearOptions: classYears,
      currentUserId: getUserId(),
      currentUserType: getUserType(),
      currentUserEmail: getUserEmail()
    }
  },

  computed: {
    filteredLeadershipPositions() {
      if (!this.filterLeadershipClass) {
        return this.leadershipPositions
      }
      return this.leadershipPositions.filter(position => {
        return position.leadership_class == this.filterLeadershipClass
      })
    },
    isStudent() {
      return this.currentUserType === 'student'
    }
  },

  watch: {
    clubInfo: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.leadership) {
            console.log('Club Info:', newVal.leadership)
          this.leadershipPositions = newVal.leadership.map(position => ({
            name: position.position,
            userId: position.user_id,
            userInfo: position.user,
            tenure_start: position.tenure_start || '',
            tenure_end: position.tenure_end || '',
            leadership_class: position.leadership_class || '',
            isEditing: false
          }));
        }
      }
    }
  },

  methods: {
    async searchMembers(query) {
      this.loading = true
      try {
        const response = await postRequest('searchClubStudents', {
          club_id: this.club_id,
          key_word: query || ''
        })
        if (response.data.code === 0) {
          this.memberOptions = response.data.data
        }
      } catch (error) {
        console.error('Error searching members:', error)
        this.$message.error('Failed to search members')
      }
      this.loading = false
    },

    async saveLeadership() {
      try {
        const response = await postRequest('updateClubLeadership', {
          club_id: this.club_id,
          leadership: this.leadershipPositions.map(position => ({
            position: position.name,
            user_id: position.userId,
            tenure_start: position.tenure_start || '',
            tenure_end: position.tenure_end || '',
            leadership_class: position.leadership_class || ''
          }))
        })

        if (response.data.code === 0) {
          this.$message.success('Leadership updated successfully')
        } else {
          this.$message.error(response.data.message || 'Failed to update leadership')
        }
      } catch (error) {
        console.error('Error saving leadership:', error)
        this.$message.error('Failed to save leadership')
      }
    },

    toggleEdit(index) {
      this.$set(this.leadershipPositions[index], 'isEditing', !this.leadershipPositions[index].isEditing)
    },

    removePosition(index) {
      this.leadershipPositions.splice(index, 1)
    },

    addPosition() {
      this.leadershipPositions.push({
        name: 'New Position',
        userId: '',
        userInfo: null,
        tenure_start: '',
        tenure_end: '',
        leadership_class: '',
        isEditing: true
      })
    },

    handleMemberSelect(userId, position) {
      const selectedMember = this.memberOptions.find(m => m.user_id === userId)
      if (selectedMember) {
        position.userInfo = selectedMember.user
      }
    },

    getMemberOptionsForRow(row) {
      // If the row has a current user that's not in memberOptions, include it
      if (row.userInfo && row.userId) {
        const hasCurrentUser = this.memberOptions.some(m => m.user_id === row.userId)
        if (!hasCurrentUser) {
          return [
            { user_id: row.userId, user: row.userInfo },
            ...this.memberOptions
          ]
        }
      }
      return this.memberOptions
    },

    ensureCurrentMemberInOptions(row) {
      // When focusing the select, load all members and ensure current member is in options
      if (this.memberOptions.length === 0) {
        this.searchMembers('')
      }
      if (row.userInfo && row.userId) {
        const hasCurrentUser = this.memberOptions.some(m => m.user_id === row.userId)
        if (!hasCurrentUser) {
          this.memberOptions.unshift({ user_id: row.userId, user: row.userInfo })
        }
      }
    },

    getUserDisplayName(position) {
      if (!position.userInfo) return ''
      console.log(position.userInfo)
      return `${position.userInfo.student_givenName_pinyin} ${position.userInfo.student_lastName_pinyin} (${position.userInfo.student_email})`
    },

    canViewCertificate(position) {
      // Certificate is only available when tenure_end is set and current date > tenure_end
      if (!position.tenure_end) return false
      const today = new Date().toISOString().split('T')[0]
      return today > position.tenure_end
    },

    isCurrentUser(position) {
      // Check if the logged-in user is a student and matches this leadership position
      if (this.currentUserType !== 'student') {
        return false
      }
      // Match by user_id or email
      if (position.userId && position.userId === this.currentUserId) {
        return true
      }
      if (position.userInfo && position.userInfo.student_email === this.currentUserEmail) {
        return true
      }
      return false
    },

    viewCertificate(position) {
      if (!position.userInfo) return

      const userName = `${position.userInfo.student_givenName_pinyin || ''} ${position.userInfo.student_lastName_pinyin || ''}`.trim()
      const schoolName = this.clubInfo.account_nameEN || this.clubInfo.account_name || ''

      const route = this.$router.resolve({
        path: '/certificate',
        query: {
          type: 'leadership',
          club_name: this.clubInfo.name,
          school_name: schoolName,
          user_name: userName,
          user_email: position.userInfo.student_email || '',
          position: position.name || '',
          tenure_start: position.tenure_start || '',
          tenure_end: position.tenure_end || ''
        }
      })
      window.open(route.href, '_blank')
    }
  }
}
</script>

<style scoped lang="scss">
.leadership-wrapper {
  min-height: calc(100vh - 64px);
  padding: 20px;
}

.leadership-container {
  width: 100%;
}

.leadership-section {
  background: white;
  padding: 24px;
  border-radius: 8px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);

  h3 {
    margin-top: 0;
    margin-bottom: 24px;
    font-size: 18px;
    font-weight: 600;
  }
}

.leadership-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.leadership-actions {
  margin-top: 24px;
  text-align: right;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
}

.filter-section {
  margin-bottom: 20px;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 4px;
}

.filter-row {
  display: flex;
  align-items: center;
}
</style> 